import { Provider } from '@unipile/db';
import { Static, Type } from '@sinclair/typebox';
import { StringEnum } from '../utils/typebox';

/**
 * Available API resources that can be accessed
 */
export const ScopeResourceSchema = StringEnum(['messaging', 'users', 'posts', 'emails', 'calendar']);

export type ScopeResource = Static<typeof ScopeResourceSchema>;

/**
 * Permission levels for each resource
 */
export const ScopePermissionSchema = StringEnum(['read', 'manage']);

export type ScopePermission = Static<typeof ScopePermissionSchema>;

/**
 * A single scope definition (e.g., "messaging:read", "posts:manage")
 */
export const ScopeSchema = Type.Object({
  resource: ScopeResourceSchema,
  permission: ScopePermissionSchema,
});

export type Scope = Static<typeof ScopeSchema>;

/**
 * Scope key format: "resource:permission" (e.g., "messaging:read")
 */
export const ScopeKeySchema = StringEnum([
  'messaging:read',
  'messaging:manage',
  'users:read',
  'users:manage',
  'posts:read',
  'posts:manage',
  'emails:read',
  'emails:manage',
  'calendar:read',
  'calendar:manage',
]);

export type ScopeKey = Static<typeof ScopeKeySchema>;

/**
 * Provider-specific resource availability
 * Defines which resources are available for each provider.
 * If a resource is available, both "read" and "manage" permissions are automatically available.
 *
 * Note on contacts:
 * - messaging:read always includes participant/contact info for that conversation
 * - users:read is for accessing the FULL contact list (connections, followers, etc.) beyond conversations
 * - WhatsApp/Telegram don't have a separate contacts API, so users resource is not available
 */
export const PROVIDER_AVAILABLE_RESOURCES: Record<Provider, ScopeResource[]> = {
  linkedin: ['messaging', 'users', 'posts'],
  google: ['emails', 'calendar'],
  outlook: ['emails', 'calendar'],
  whatsapp: ['messaging'],
  telegram: ['messaging'],
  instagram: ['messaging', 'users', 'posts'],
  facebook: ['messaging'],
  imap: ['emails'],
  mock: [],
};

/**
 * Get available scopes for a provider based on available resources.
 * If a resource is available, both "read" and "manage" permissions are included.
 */
export function getProviderScopes(provider: Provider): ScopeKey[] {
  const resources = PROVIDER_AVAILABLE_RESOURCES[provider] || [];
  const scopes: ScopeKey[] = [];

  for (const resource of resources) {
    scopes.push(`${resource}:read`);
    scopes.push(`${resource}:manage`);
  }

  return scopes;
}

/**
 * @deprecated Use PROVIDER_AVAILABLE_RESOURCES and getProviderScopes() instead
 * Kept for backward compatibility
 */
export const PROVIDER_AVAILABLE_SCOPES: Record<Provider, ScopeKey[]> = {
  linkedin: getProviderScopes('linkedin'),
  google: getProviderScopes('google'),
  outlook: getProviderScopes('outlook'),
  whatsapp: getProviderScopes('whatsapp'),
  telegram: getProviderScopes('telegram'),
  instagram: getProviderScopes('instagram'),
  facebook: getProviderScopes('facebook'),
  imap: getProviderScopes('imap'),
  mock: [],
};

/**
 * Scopes configuration for hosted auth
 * Maps each scope key to whether it's enabled
 */
export const HostedAuthScopesConfigSchema = Type.Record(Type.String(), Type.Boolean());

export type HostedAuthScopesConfig = Static<typeof HostedAuthScopesConfigSchema>;

/**
 * Scope descriptions for display in consent screen
 * These are default English descriptions - actual display uses i18n translations
 */
export const SCOPE_DESCRIPTIONS: Record<ScopeKey, { title: string; description: string }> = {
  'messaging:read': {
    title: 'Read messages',
    description: 'View your conversations, messages, and contact information of participants',
  },
  'messaging:manage': {
    title: 'Manage messages',
    description: 'Send, edit, and delete messages on your behalf, and manage conversations',
  },
  'users:read': {
    title: 'Read users / contacts / followers',
    description: 'Access your full contact list, connections, or followers beyond conversations',
  },
  'users:manage': {
    title: 'Manage users / contacts / followers',
    description: 'Send connection requests, add or remove contacts on your behalf',
  },
  'posts:read': {
    title: 'Read posts',
    description: 'View your posts, feed, and social content',
  },
  'posts:manage': {
    title: 'Manage posts',
    description: 'Create, edit, delete posts, and interact with content on your behalf',
  },
  'emails:read': {
    title: 'Read emails',
    description: 'View your emails, folders, and drafts',
  },
  'emails:manage': {
    title: 'Manage emails',
    description: 'Send, draft, organize, and delete emails on your behalf',
  },
  'calendar:read': {
    title: 'Read calendar',
    description: 'View your calendars and events',
  },
  'calendar:manage': {
    title: 'Manage calendar',
    description: 'Create, edit, and delete calendar events on your behalf',
  },
};

/**
 * Helper function to parse a scope key into resource and permission
 */
export function parseScopeKey(key: ScopeKey): Scope {
  const [resource, permission] = key.split(':');
  return { resource: resource as ScopeResource, permission: permission as ScopePermission };
}

/**
 * Helper function to create a scope key from resource and permission
 */
export function createScopeKey(resource: ScopeResource, permission: ScopePermission): ScopeKey {
  return `${resource}:${permission}`;
}

/**
 * Get available resources for a provider
 */
export function getProviderResources(provider: Provider): ScopeResource[] {
  return PROVIDER_AVAILABLE_RESOURCES[provider] || [];
}

/**
 * Get providers that support a specific resource
 */
export function getProvidersForResource(resource: ScopeResource): Provider[] {
  return (Object.keys(PROVIDER_AVAILABLE_RESOURCES) as Provider[]).filter((provider) =>
    PROVIDER_AVAILABLE_RESOURCES[provider].includes(resource),
  );
}
